import React, { forwardRef } from 'react';
import { UsersIcon } from '@phosphor-icons/react';

import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import type { PollOptionResult } from './types';
import type { HeadingLevel } from 'volto-ministerio-publico-federal/types/heading';

export interface PollResultsProps {
  question: string;
  options: PollOptionResult[];
  total: number;
  justVoted: boolean;
  votedOptionId?: string | null;
  /**
   * Nível do heading da pergunta. O default `h3` assume a enquete como
   * subseção de um bloco que já tem h2; num contexto mais raso, passe `h2`.
   */
  as?: HeadingLevel;
}

function formatVotes(count: number): string {
  return count === 1 ? '1 voto' : `${count} votos`;
}

function formatParticipants(count: number): string {
  return count === 1 ? '1 participante' : `${count} participantes`;
}

const PollResults = forwardRef<HTMLDivElement, PollResultsProps>(
  function PollResults(
    { question, options, total, justVoted, votedOptionId, as: Heading = 'h3' },
    ref,
  ) {
    return (
      <section
        ref={ref}
        tabIndex={-1}
        aria-live="polite"
        className="poll-results"
      >
        <header className="poll-block__header">
          <Heading className="poll-block__question">{question}</Heading>
          <p className="poll-block__subtitle">
            {justVoted
              ? 'Você votou. Veja como a comunidade está se posicionando.'
              : 'Você já votou nesta enquete. Veja como a comunidade está se posicionando.'}
          </p>
        </header>

        <ul className="poll-results__list">
          {options.map((option) => {
            const isVoted = option.id === votedOptionId;
            return (
              <li
                key={option.id}
                className="poll-result"
                data-voted={isVoted ? 'true' : 'false'}
                aria-label={`${option.label}: ${option.percentage}% — ${formatVotes(option.votes)}`}
              >
                <span
                  className="poll-result__fill"
                  style={{ width: `${option.percentage}%` }}
                  aria-hidden="true"
                />
                <div className="poll-result__content">
                  <div className="poll-result__meta">
                    <span className="poll-result__name">{option.label}</span>
                    {isVoted ? (
                      <span className="poll-result__pill">Seu voto</span>
                    ) : null}
                    <span className="poll-result__votes">
                      {formatVotes(option.votes)}
                    </span>
                  </div>
                  <span className="poll-result__percentage">
                    {option.percentage}%
                  </span>
                </div>
              </li>
            );
          })}
        </ul>

        <footer className="poll-block__footer">
          <span className="poll-block__footer-item">
            <UsersIcon aria-hidden="true" />
            {formatParticipants(total)}
          </span>
          <MPFButton
            variant="unstyled"
            className="poll-block__change"
            disabled
            aria-disabled="true"
            title="Mudança de voto ainda não disponível nesta enquete"
          >
            Mudar meu voto
          </MPFButton>
        </footer>
      </section>
    );
  },
);

export default PollResults;
